// ShareBite protected-operation authorization decision (Task 8.1).
//
// Pure, platform-independent decision function the Backend uses to authorize a
// protected operation. The decision is a function of the caller's
// authentication state, Role, and Verification Status together with the
// operation being attempted. The user interface is untrusted: this same
// decision is enforced server-side on every protected request (Req 27.1).
//
// The rules encoded here:
//   - An unauthenticated caller is always denied (Req 27.2).
//   - A caller whose Role is not permitted for the operation is denied
//     (Req 5.7, 23.2, 23.13, 27.3).
//   - For role-dashboard operations, the caller's Verification Status must be
//     exactly Approved (Req 5.1, 5.4, 5.5, 27.4).
//   - Only a Super Admin may create or manage Admin accounts (Req 2.10, 23.13).
//   - Only an Admin or Super Admin may access the Admin Panel or perform
//     verification actions (Req 5.7, 23.2).
//
// Design reference: design.md "Property 16: Protected-operation authorization
// decision".
// Requirements: 2.10, 5.1, 5.4, 5.5, 5.7, 23.2, 23.13, 27.1, 27.2, 27.3, 27.4.
/** Role-dashboard operations that additionally require Approved status. */
const ROLE_DASHBOARD_OPERATIONS = {
    RestaurantDashboard: true,
    NGODashboard: true,
    AdminPanel: false,
    Verification: false,
    ManageAdminAccounts: false,
};
/**
 * The set of Roles permitted to attempt each operation (before any status
 * check). A caller whose Role is absent from this set is denied with
 * `insufficient permissions`.
 */
const PERMITTED_ROLES = {
    RestaurantDashboard: ["Restaurant"],
    NGODashboard: ["NGO"],
    AdminPanel: ["Admin", "SuperAdmin"],
    Verification: ["Admin", "SuperAdmin"],
    ManageAdminAccounts: ["SuperAdmin"],
};
/**
 * Decide whether the caller is authorized to perform the protected operation.
 *
 * The decision is granted if and only if ALL hold, evaluated in this order:
 *  1. the caller is authenticated (else `authentication required`), AND
 *  2. the caller's Role is permitted for the operation (else
 *     `insufficient permissions`), AND
 *  3. for role-dashboard operations only, the caller's Verification Status is
 *     exactly `Approved` (else `account not approved`).
 *
 * Authentication is checked before role, and role before status, so an
 * unauthenticated caller never leaks role/status information and a forbidden
 * role is never reported as merely unapproved.
 *
 * This function is pure: it reads only its arguments, mutates nothing, and
 * produces no side effects. On denial the caller MUST make no state changes.
 *
 * @param context   The trusted server-side caller context.
 * @param operation The protected operation being attempted.
 * @returns An {@link AuthorizationDecision}.
 */
export function authorize(context, operation) {
    if (!context.authenticated) {
        return { granted: false, reason: "authentication required" };
    }
    const permitted = PERMITTED_ROLES[operation];
    if (context.role === null || !permitted.includes(context.role)) {
        return { granted: false, reason: "insufficient permissions" };
    }
    if (ROLE_DASHBOARD_OPERATIONS[operation] &&
        context.verificationStatus !== "Approved") {
        return { granted: false, reason: "account not approved" };
    }
    return { granted: true, reason: null };
}
/** Convenience predicate: `true` iff {@link authorize} grants the operation. */
export function isAuthorized(context, operation) {
    return authorize(context, operation).granted;
}
//# sourceMappingURL=authorization.js.map